// Navigation: committing decisions to the graph and deciding where to go next.

import { storage } from "../lib/storage";
import { clamp } from "../lib/text";
import { bus, renderAll, toast } from "./bus";
import { scheduleConstraintScan } from "./constraints";
import { applyDecision, generateNode, proposeDecision } from "./navigator";
import { save } from "./persistence";
import { prefetchChildren, waitForPrefetch } from "./prefetch";
import { registerNode, state, type DecisionNode } from "./state";

// ---- navigation strategy (where to go after answering) ---------------------
// depth: drill into the consequence of what you just chose (classic).
// breadth: open every section first, then round-robin the shallowest open
//   question — a doc visibly fills wide before it fills deep.
// smart: descend only when the new question is high-stakes; otherwise breadth.
export type NavStrategy = "depth" | "breadth" | "smart";

let navStrategy: NavStrategy = (() => {
  const s = storage.get("lattice.nav");
  return s === "breadth" || s === "smart" ? s : "depth";
})();

export function setNavStrategy(s: NavStrategy): void {
  if (!["depth", "breadth", "smart"].includes(s)) return;
  navStrategy = s;
  storage.set("lattice.nav", s);
}
export function getNavStrategy(): NavStrategy { return navStrategy; }

/** Generate and commit the root node (the top-level branch menu). */
export async function createRoot(): Promise<DecisionNode> {
  const root = await generateNode({ isRoot: true });
  registerNode(root);
  state.rootId = root.id;
  state.currentId = root.id;
  save();
  return root;
}

export async function pick(i: number): Promise<void> {
  const node = state.nodes.get(state.currentId!);
  if (!node || !node.options[i]) return;
  const prevChoice = node.chosenIndex;
  const prevConfidence = node.confidence;
  node.chosenIndex = i;
  node.confidence = Math.max(node.confidence, 0.82); // deciding resolves this node

  // Already-committed child (revisiting a branch) → instant.
  if (node.childId[i]) {
    const existing = state.nodes.get(node.childId[i]!)!;
    existing.lastLatency = 0;
    return descendTo(existing);
  }

  const pf = node.prefetch[i];
  let child: DecisionNode;
  if (pf && pf.status === "ready") {
    child = pf.node!;
    child.lastLatency = 0;         // prefetched → 0 ms perceived
  } else {
    // cache miss — flash selection, then generate live (or wait on an in-flight prefetch)
    bus.emit("option:highlight", { index: i });
    bus.emit("render:node", { node, opts: { thinking: true } });
    try {
      child = pf?.status === "loading"
        ? await waitForPrefetch(node, i)
        : await generateNode({ parentNode: node, optionIndex: i });
    } catch {
      // no child was committed — the decision didn't happen, so don't let the
      // node LOOK decided (it would lose its options in the graph/map views)
      node.chosenIndex = prevChoice;
      node.confidence = prevConfidence;
      toast("navigator hiccup — try again");
      bus.emit("render:node", { node });
      return;
    }
  }

  // The single commit point: a detached node joins the graph here, nowhere else.
  registerNode(child);
  node.childId[i] = child.id;
  // a resolved design decision (not the root section menu) changes the doc
  if (node.depth > 0) bus.emit("decision", { nodeId: node.id });
  await afterPick(node, child);
}

async function afterPick(parent: DecisionNode, child: DecisionNode): Promise<void> {
  if (navStrategy === "depth") return descendTo(child);
  if (parent.id === state.rootId) return descendTo(child);        // you chose a section — go there
  if (navStrategy === "smart" && child.stakes === "high") return descendTo(child);

  // breadth: an unopened top-level section always comes first
  const root = state.nodes.get(state.rootId!)!;
  const j = root.options.findIndex((_, k) => root.childId[k] == null);
  if (j >= 0) {
    state.currentId = root.id;
    return pick(j);            // generates that section's first question and lands on it
  }
  // all sections open: shallowest unanswered question, oldest first
  const seqOf = (n: DecisionNode) => parseInt(n.id.slice(1), 10) || 0;
  const open = [...state.nodes.values()]
    .filter((n) => n.depth > 0 && n.chosenIndex == null && n.options.length)
    .sort((a, b) => a.depth - b.depth || seqOf(a) - seqOf(b));
  return open.length ? jumpTo(open[0]!.id) : descendTo(child);
}

/** Make a committed node current, render, and stage the next level of prefetch. */
function descendTo(child: DecisionNode): void {
  state.currentId = child.id;
  save();
  renderAll();
  prefetchChildren(child);         // one level of lookahead → next descent is instant
  scheduleConstraintScan();        // background coherence pass over the whole trace
}

export function goBack(): void {
  const node = state.nodes.get(state.currentId!);
  if (!node || !node.parentId) return;
  state.currentId = node.parentId;
  save();
  renderAll();
}

export function jumpTo(id: string): void {
  if (!state.nodes.has(id)) return;
  state.currentId = id;
  save();
  renderAll();
  prefetchChildren(state.nodes.get(id)!);
}

const normWords = (s: string) => s.toLowerCase().replace(/[^a-z0-9 ]+/g, " ").split(/\s+/).filter((w) => w.length > 2);

/** Token-overlap similarity in [0,1] between two short labels. */
export function tokenOverlap(a: string, b: string): number {
  const target = new Set(normWords(a));
  const words = normWords(b);
  if (!target.size || !words.length) return 0;
  const hit = words.filter((w) => target.has(w)).length;
  return hit / Math.max(words.length, target.size);
}

/** Loose label match: does the reviewer's suggested option name an actual option? */
export function matchOptionLabel(node: DecisionNode, text: string): number | null {
  let best = -1, bestScore = 0;
  node.options.forEach((o, i) => {
    const score = tokenOverlap(text, o.label);
    if (score > bestScore) { bestScore = score; best = i; }
  });
  return bestScore >= 0.5 ? best : null;
}

/**
 * Accept a reviewer's stronger option: re-pick it, clear the stale verdict,
 * and re-route the decision into the doc (merging over the old text).
 */
export async function applySuggestion(nodeId: string, optionIndex: number): Promise<void> {
  const node = state.nodes.get(nodeId);
  if (!node || !node.options[optionIndex] || node.chosenIndex === optionIndex) return;
  node.verdict = undefined;                                    // judged the old choice
  state.doc.folded = state.doc.folded.filter((id) => id !== nodeId);   // re-fold with the new choice
  state.currentId = nodeId;
  toast(`switched to reviewer suggestion: ${node.options[optionIndex]!.label}`);
  await pick(optionIndex);
}

export async function injectFact(text: string): Promise<void> {
  const fact = text.trim();
  if (!fact) return;
  state.facts.push(fact);
  const node = state.nodes.get(state.currentId!)!;
  // a new fact means the agent knows there's context it hadn't accounted for → confidence drops
  node.confidence = clamp(node.confidence - 0.35, 0.08, 0.9);
  node.prefetch = {}; node.childId = {}; // context changed downstream
  toast(`fact injected · confidence dropped · re-evaluating`);
  bus.emit("render:node", { node, opts: { thinking: true, keepQuestion: true } });
  try {
    // Refresh this node's decision in place — no throwaway node, so nothing
    // leaks into the graph.
    const parent = node.parentId ? state.nodes.get(node.parentId)! : null;
    const data = await proposeDecision({
      parentNode: parent,
      optionIndex: parent ? parent.chosenIndex : null,
      isRoot: !node.parentId,
    });
    applyDecision(node, data);
  } catch { /* keep old options */ }
  save();
  renderAll();
  prefetchChildren(node);
  scheduleConstraintScan();        // a new fact can create (or dissolve) a tension
}
